import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getHelp, updateHelp } from '../../services/helpService';
import { sendSupportMessage } from '../../services/supportService';

const HelpPage = () => {
  const { user } = useAuth();

  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sendMsg, setSendMsg] = useState('');
  const [sendErr, setSendErr] = useState('');

  const fetchHelp = async () => {
    setLoading(true);
    try {
      const data = await getHelp();
      setContent(data.content);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load the help page');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHelp();
  }, []);

  const startEdit = () => {
    setDraft(content);
    setEditing(true);
    setSaveMsg('');
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg('');
    try {
      await updateHelp(draft);
      setContent(draft);
      setEditing(false);
      setSaveMsg('Saved ✓');
    } catch (err) {
      setSaveMsg(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSendSupport = async (e) => {
    e.preventDefault();
    setSending(true);
    setSendErr('');
    setSendMsg('');
    try {
      const data = await sendSupportMessage({ subject, message });
      setSendMsg(data.message);
      setSubject('');
      setMessage('');
    } catch (err) {
      setSendErr(err.response?.data?.message || 'Failed to send your message');
    } finally {
      setSending(false);
    }
  };

  if (loading) return <p style={styles.status}>Loading help page...</p>;

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h2 style={styles.title}>❓ Help</h2>
          {user?.is_admin && !editing && (
            <button onClick={startEdit} style={styles.editBtn}>Edit Page</button>
          )}
        </div>

        {error && <p style={styles.error}>{error}</p>}

        {editing ? (
          <>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              style={styles.textarea}
              rows={16}
            />
            <div style={styles.editActions}>
              <button onClick={() => setEditing(false)} style={styles.cancelBtn} disabled={saving}>Cancel</button>
              <button onClick={handleSave} style={styles.saveBtn} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
              {saveMsg && <span style={styles.saveMsg}>{saveMsg}</span>}
            </div>
          </>
        ) : (
          <div style={styles.content}>
            {content.split(/\n\s*\n/).map((paragraph, i) => (
              <p key={i} style={styles.paragraph}>{paragraph}</p>
            ))}
          </div>
        )}
      </div>

      <div style={styles.card}>
        <h3 style={styles.subTitle}>✉️ Contact Support</h3>
        <p style={styles.supportIntro}>
          Questions, bug reports, or feature requests — send us a message and we'll get back to you.
        </p>
        <form onSubmit={handleSendSupport}>
          <input
            type="text"
            placeholder="Subject (optional)"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            style={styles.subjectInput}
            disabled={sending}
          />
          <textarea
            placeholder="What's on your mind?"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            style={styles.messageInput}
            rows={5}
            disabled={sending}
            required
          />
          {sendErr && <p style={styles.error}>{sendErr}</p>}
          {sendMsg && <p style={styles.successMsg}>{sendMsg}</p>}
          <button type="submit" style={styles.saveBtn} disabled={sending}>
            {sending ? 'Sending...' : 'Send Message'}
          </button>
        </form>
      </div>
    </div>
  );
};

const styles = {
  container: { maxWidth: '760px', margin: '0 auto' },
  card: {
    backgroundColor: 'white',
    padding: '32px',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    marginBottom: '24px',
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  title: { color: '#e67e22', margin: 0 },
  subTitle: { color: '#e67e22', marginTop: 0, marginBottom: '8px' },
  supportIntro: { color: '#888', fontSize: '13px', marginBottom: '16px' },
  editBtn: {
    padding: '8px 16px', backgroundColor: '#e67e22', color: 'white', border: 'none',
    borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '13px',
  },
  content: { color: '#444', lineHeight: 1.7, fontSize: '15px' },
  paragraph: { marginBottom: '16px', whiteSpace: 'pre-wrap' },
  textarea: {
    width: '100%', padding: '14px', borderRadius: '8px', border: '1px solid #ddd',
    fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical',
  },
  editActions: { display: 'flex', alignItems: 'center', gap: '12px', marginTop: '14px' },
  cancelBtn: {
    padding: '10px 20px', backgroundColor: 'transparent', border: '1px solid #ddd',
    borderRadius: '8px', cursor: 'pointer', color: '#666',
  },
  saveBtn: {
    padding: '10px 24px', backgroundColor: '#e67e22', color: 'white', border: 'none',
    borderRadius: '8px', fontWeight: '600', cursor: 'pointer', marginTop: '12px',
  },
  saveMsg: { color: '#27ae60', fontSize: '13px', fontWeight: '600' },
  subjectInput: {
    width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #ddd',
    fontSize: '14px', marginBottom: '10px', boxSizing: 'border-box',
  },
  messageInput: {
    width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #ddd',
    fontSize: '14px', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box',
  },
  successMsg: { color: '#27ae60', fontSize: '13px', marginTop: '10px' },
  error: { color: '#e74c3c', fontSize: '13px', marginTop: '10px' },
  status: { textAlign: 'center', color: '#999' },
};

export default HelpPage;
